import { useCallback, useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isCountingModeFailure } from '../lib/capture-flow'
import type { CaptureFailureKind } from '../lib/capture-flow'
import type { CountAssortmentItem } from '@/api/endpoints/inventory-count-sessions'
import { inventoryCountSessionsApi } from '@/api/endpoints/inventory-count-sessions'

const EMPTY: Array<CountAssortmentItem> = []

export interface CountAssortmentState {
  /** True once the payload has arrived; every derived figure is meaningless before that. */
  loaded: boolean
  items: Array<CountAssortmentItem>
  /** Mirrors the location's setting; false means a blind count and no `expectedQty` anywhere. */
  showExpected: boolean
  /** The must-count set: a stock record with on-hand > 0 at session start. */
  mustCountSkus: Set<string>
  /** Resolves a scanned code against the local barcode/SKU indexes, or null if unknown here. */
  resolveLocally: (code: string) => CountAssortmentItem | null
  itemBySku: (sku: string) => CountAssortmentItem | null
}

/**
 * The counted location's whole assortment, fetched once when the capture screen opens and held
 * for the session (`staleTime: Infinity`, keyed by session id).
 *
 * One fetch, deliberately: search, the outstanding list and EAN resolution all run off this list,
 * so none of them needs a round trip while the counter walks the store. The trade-off is accepted
 * in design.md — an article that reaches stock during the count will not appear in the open list
 * until the next session, while staying scannable and capturable.
 *
 * A barcode or SKU found here opens the quantity sheet immediately, connection or not. Only a
 * product-info QR code, or a code this location does not carry, still asks the server via
 * `resolveCode` — which is also what keeps the unknown-code path working.
 */
export function useCountAssortment(
  restaurantId: number | null,
  sessionId: string | null,
): CountAssortmentState {
  const query = useQuery({
    queryKey: ['count-assortment', restaurantId, sessionId],
    queryFn: () => inventoryCountSessionsApi.assortment(restaurantId as number),
    enabled: restaurantId != null && sessionId != null,
    staleTime: Number.POSITIVE_INFINITY,
  })

  const items = query.data?.items ?? EMPTY

  const indexes = useMemo(() => {
    const bySku = new Map<string, CountAssortmentItem>()
    const byBarcode = new Map<string, CountAssortmentItem>()
    for (const item of items) {
      bySku.set(item.sku.toLowerCase(), item)
      // A barcode is optional and not unique by contract; first row wins, as the server's own
      // resolve does.
      if (item.barcode != null && item.barcode !== '') {
        const key = item.barcode.toLowerCase()
        if (!byBarcode.has(key)) byBarcode.set(key, item)
      }
    }
    return { bySku, byBarcode }
  }, [items])

  const mustCountSkus = useMemo(
    () =>
      new Set(items.filter((item) => item.mustCount).map((item) => item.sku)),
    [items],
  )

  const resolveLocally = useCallback(
    (code: string) => {
      const key = code.trim().toLowerCase()
      if (key === '') return null
      return indexes.byBarcode.get(key) ?? indexes.bySku.get(key) ?? null
    },
    [indexes],
  )

  const itemBySku = useCallback(
    (sku: string) => indexes.bySku.get(sku.toLowerCase()) ?? null,
    [indexes],
  )

  // Memoized as a whole: the capture screen derives the scanner's `onHit` from this, and an
  // object rebuilt on every render would tear down and re-create the detection interval with it.
  return useMemo(
    () => ({
      loaded: query.data != null,
      items,
      showExpected: query.data?.showExpected ?? false,
      mustCountSkus,
      resolveLocally,
      itemBySku,
    }),
    [query.data, items, mustCountSkus, resolveLocally, itemBySku],
  )
}

/**
 * Reads the assortment again after the server refused a quantity for the article's counting mode
 * (`whole_quantity_required`, `invalid_quantity`; CHG-2026-09-stock-usage-and-price-twins): the
 * device's "Teilmengen zählen" flag was stale, and the next pad for that article must match the
 * server again — whole numbers, exactly as before.
 */
export function useRefreshAssortmentOnModeFailure(
  failure: CaptureFailureKind | null,
  restaurantId: number | null,
  sessionId: string | null,
) {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!isCountingModeFailure(failure)) return
    void queryClient.invalidateQueries({
      queryKey: ['count-assortment', restaurantId, sessionId],
    })
  }, [failure, queryClient, restaurantId, sessionId])
}
